import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, HStack, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { WizardStepIndicator } from '@/components/wizard/WizardStepIndicator';
import { WizardNavigation } from '@/components/wizard/WizardNavigation';
import { CloseButton } from '@/components/ui/CloseButton';
import { LanguageSelector } from '@/components/ui/LanguageSelector';

import { WelcomeStep } from '@/components/wizard/steps/WelcomeStep';
import { EmailOtpFlow } from '@/features/auth/components/EmailOtpFlow';
import { LocationStep, type LocationStepData } from '@/components/wizard/steps/LocationStep';
import { MusicStep, type MusicStepData } from '@/components/wizard/steps/MusicStep';
import { ScenarioStep, type ScenarioStepData } from '@/components/wizard/steps/ScenarioStep';
import { PreviewStep } from '@/components/wizard/steps/PreviewStep';
import { SubscriptionStep } from '@/components/wizard/steps/SubscriptionStep';
import type { WizardPreview } from '../../services/wizardApi';

import { TransitionScreen } from '../../features/onboarding/components/TransitionScreen';
import { getTransitionByFrom, type TransitionContent } from '@/components/wizard/transitions/transitionContent';

import { useWizard, type WizardStep } from '../../hooks/useWizard';
import { wizardApi } from '../../services/wizardApi';
import { syncCarouselCompletionToBackend } from '../../services/carouselStorage';
import { useMusicPreferences } from '../../hooks/useMusicPreferences';
import { LeaveWizardPopup } from '@/features/wizard/LeaveWizardPopup';

const WIZARD_STEPS: { id: WizardStep; label: string }[] = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'auth', label: 'Account' },
  { id: 'location', label: 'Location' },
  { id: 'music', label: 'Music' },
  { id: 'scenario', label: 'Scenario' },
  { id: 'preview', label: 'Review' },
  { id: 'subscription', label: 'Plan' },
];

export function WizardPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [previewData, setPreviewData] = useState<WizardPreview | null>(null);
  const [showingTransition, setShowingTransition] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<TransitionContent | null>(null);
  const [showLeavePopup, setShowLeavePopup] = useState(false);

  const wizard = useWizard({
    onComplete: () => {
      navigate('/app');
    },
  });

  const musicPrefs = useMusicPreferences();

  useEffect(() => {
    if (wizard.currentStep === 'preview' && wizard.isAuthenticated) {
      wizardApi.getPreviewData()
        .then(setPreviewData)
        .catch(console.error);
    }
  }, [wizard.currentStep, wizard.isAuthenticated]);

  useEffect(() => {
    if (wizard.isAuthenticated) {
      wizardApi.getProgress().then(async () => {
        const [{ apiClient }, { getApiUrl }] = await Promise.all([
          import('../../services/apiClient'),
          import('../../lib/runtimeConfig'),
        ]);
        const token = await apiClient.resolveToken();
        if (token) {
          syncCarouselCompletionToBackend(getApiUrl() || 'http://localhost:5001', token);
        }
      }).catch(() => {});
    }
  }, [wizard.isAuthenticated]);

  const showTransitionFor = useCallback((fromStep: string) => {
    const transition = getTransitionByFrom(fromStep);
    if (transition) {
      setPendingTransition(transition);
      setShowingTransition(true);
      return true;
    }
    return false;
  }, []);

  const handleTransitionContinue = useCallback(() => {
    setShowingTransition(false);
    setPendingTransition(null);
    wizard.goToNext();
  }, [wizard]);

  const handleTransitionSkip = useCallback(() => {
    setShowingTransition(false);
    setPendingTransition(null);
    wizard.goToNext();
  }, [wizard]);

  const handleWelcomeContinue = useCallback(() => {
    wizard.goToNext();
  }, [wizard]);

  const handleLocationSubmit = useCallback(async (data: LocationStepData) => {
    await wizard.completeLocationStep({
      organizationName: data.organizationName || data.name || '',
      locationName: data.locationName || data.name || '',
      locationType: data.locationType || data.venueType || '',
      address: data.address,
    });
    showTransitionFor('location');
  }, [wizard, showTransitionFor]);

  const handleMusicSubmit = useCallback(async (data: MusicStepData) => {
    await wizard.completeMusicStep({
      genres: data.genres,
      moods: data.moods,
    });
  }, [wizard]);

  const handleScenarioSubmit = useCallback(async (data: ScenarioStepData) => {
    await wizard.completeScenarioStep({
      name: data.name,
      description: data.description,
    });
  }, [wizard]);

  const handlePreviewConfirm = useCallback(async () => {
    await wizard.completePreviewStep();
  }, [wizard]);

  const handlePreviewEdit = useCallback((step: string) => {
    wizard.goToStep(step as WizardStep);
  }, [wizard]);

  const handleSubscriptionSubmit = useCallback(async (plan: string) => {
    await wizard.completeSubscriptionStep(plan);
  }, [wizard]);

  const renderStep = () => {
    if (showingTransition && pendingTransition) {
      return (
        <TransitionScreen
          titleKey={pendingTransition.titleKey}
          subtitleKey={pendingTransition.subtitleKey}
          skippable={pendingTransition.skippable}
          onContinue={handleTransitionContinue}
          onSkip={pendingTransition.skippable ? handleTransitionSkip : undefined}
        />
      );
    }

    switch (wizard.currentStep) {
      case 'welcome':
        return <WelcomeStep onContinue={handleWelcomeContinue} />;
      case 'auth':
        // In-app email-OTP (same flow as the standalone /signup screen). On
        // success refreshAuth() flips the wizard to authenticated; onAuthComplete
        // advances to the next step — a brand-new org has no saved progress to
        // resume to, and we're already on /onboarding/wizard.
        return <EmailOtpFlow embedded onAuthComplete={wizard.goToNext} />;
      case 'location':
        return (
          <LocationStep
            onSubmit={handleLocationSubmit}
            isLoading={wizard.isLoading}
            error={wizard.error}
          />
        );
      case 'music':
        return (
          <MusicStep
            onSubmit={handleMusicSubmit}
            isLoading={wizard.isLoading || musicPrefs.isLoading}
            error={wizard.error || musicPrefs.error}
            availableGenres={musicPrefs.genres.length > 0 ? musicPrefs.genres.map(g => ({ value: g.id, label: g.name, icon: g.icon })) : undefined}
            availableMoods={musicPrefs.moods.length > 0 ? musicPrefs.moods.map(m => ({ value: m.id, label: m.name, icon: m.icon })) : undefined}
          />
        );
      case 'scenario':
        return (
          <ScenarioStep
            onSubmit={handleScenarioSubmit}
            isLoading={wizard.isLoading}
            error={wizard.error}
            locationName={wizard.progress?.stepData?.locationId ? 'Your Location' : undefined}
          />
        );
      case 'preview':
        return (
          <PreviewStep
            previewData={previewData}
            onConfirm={handlePreviewConfirm}
            onEdit={handlePreviewEdit}
            isLoading={wizard.isLoading}
            error={wizard.error}
          />
        );
      case 'subscription':
        return (
          <SubscriptionStep
            onSubmit={handleSubscriptionSubmit}
            isLoading={wizard.isLoading}
            error={wizard.error}
          />
        );
      default:
        return null;
    }
  };

  if (wizard.isLoading && !wizard.currentStep) {
    return (
      <Box display="flex" flex={1} justifyContent="center" alignItems="center" bg="white">
        <Spinner size="xl" color="brand.500" />
      </Box>
    );
  }

  const currentStepIndex = WIZARD_STEPS.findIndex((s) => s.id === wizard.currentStep);
  const showNavigation = wizard.currentStep !== 'welcome' && wizard.currentStep !== 'auth';
  const useOnboardingBg = wizard.currentStep === 'welcome' || wizard.currentStep === 'auth' || wizard.currentStep === 'location' || wizard.currentStep === 'music' || wizard.currentStep === 'scenario' || showingTransition;

  const stepContent = (
    <>
      {/* Top Nav: Close + Language */}
      <HStack justify="space-between" align="center" px={6} pt={4} pb={2}>
        <CloseButton
          onPress={() => {
            const leaveConfirmSteps: WizardStep[] = ['location', 'music', 'scenario', 'preview', 'subscription'];
            if (leaveConfirmSteps.includes(wizard.currentStep)) {
              setShowLeavePopup(true);
            } else {
              navigate('/app');
            }
          }}
          testID="wizard-close-button"
        />
        <LanguageSelector
          currentLocale={i18n.language || 'en'}
          onLocaleChange={(locale) => i18n.changeLanguage(locale)}
          testID="wizard-language-selector"
        />
      </HStack>

      {/* Step Indicator */}
      {wizard.currentStep !== 'welcome' && (
        <WizardStepIndicator
          steps={WIZARD_STEPS.filter((s) => s.id !== 'welcome')}
          currentStep={Math.max(0, currentStepIndex - 1)}
          onStepClick={(index) => {
            const step = WIZARD_STEPS[index + 1];
            if (step && wizard.isStepComplete(step.id)) {
              wizard.goToStep(step.id);
            }
          }}
        />
      )}

      {/* Current Step Content */}
      <Box flex={1}>
        {renderStep()}
      </Box>

      {/* Navigation (Back button) */}
      {showNavigation && wizard.canGoPrevious && (
        <WizardNavigation
          isFirstStep={false}
          onBack={wizard.goToPrevious}
          isLoading={wizard.isLoading}
        />
      )}
    </>
  );

  return (
    <Box display="flex" flexDirection="column" flex={1} data-testid="wizard-screen">
      <LeaveWizardPopup
        visible={showLeavePopup}
        onConfirm={() => {
          setShowLeavePopup(false);
          navigate('/app');
        }}
        onCancel={() => setShowLeavePopup(false)}
      />
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        bg={useOnboardingBg ? 'onboarding.cream' : 'white'}
      >
        {stepContent}
      </Box>
    </Box>
  );
}

export default WizardPage;
